import { findMpgByVin } from './fuelEconomyUtils';
import type {
    BaggageFuelEconomyPayload,
    FuelSavingFormValues,
    Period,
    TradeSectionData,
    VehicleFuelEconomy,
    VehicleTab
} from '../types';
import type { IDropdownOption } from '@makemydeal/dr-ui-bricks-types';

export const MILES_PER_WEEK: Period = 'WEEK';
export const MILES_PER_MONTH: Period = 'MONTH';
export const MILES_PER_YEAR: Period = 'YEAR';

const EMPTY_FUEL_SAVINGS = {
    yearlySavings: 0,
    monthlySavings: 0,
    threeYearSavings: 0
};

const isNonNegativeFiniteNumber = (value: number): boolean => Number.isFinite(value) && value >= 0;

const calculateAnnualFuelCost = (yearlyMiles: number, mpg: number, pricePerGallon: number): number => {
    if (mpg <= 0) {
        return 0;
    }

    const annualFuelCost = (yearlyMiles / mpg) * pricePerGallon;

    return Number.isFinite(annualFuelCost) ? annualFuelCost : 0;
};

/**
 * Get the period multiplier to convert miles driven to yearly miles.
 * @param milesPer - The period unit (MONTH, YEAR, or WEEK)
 * @returns The multiplier to convert to yearly miles
 */
export const getPeriodMultiplier = (milesPer: Period): number => {
    if (milesPer === MILES_PER_MONTH) {
        return 12;
    }

    if (milesPer === MILES_PER_YEAR) {
        return 1;
    }

    return 52; // WEEK
};

/**
 * Calculate fuel savings for a trade-in comparison.
 * @param milesDriven - Miles driven in the specified period
 * @param milesPer - The period unit
 * @param newVehicleMpg - MPG of the new vehicle
 * @param tradeInMpg - MPG of the trade-in vehicle
 * @param newVehiclePrice - Price per gallon of new vehicle's fuel
 * @param tradePrice - Price per gallon of trade-in vehicle's fuel
 * @returns Object containing yearly and monthly savings
 */
export const calculateFuelSavings = (
    milesDriven: number,
    milesPer: Period,
    newVehicleMpg: number,
    tradeInMpg: number,
    newVehiclePrice: number,
    tradePrice: number
) => {
    if (
        !isNonNegativeFiniteNumber(milesDriven) ||
        !isNonNegativeFiniteNumber(newVehiclePrice) ||
        !isNonNegativeFiniteNumber(tradePrice) ||
        !Number.isFinite(newVehicleMpg) ||
        !Number.isFinite(tradeInMpg)
    ) {
        return EMPTY_FUEL_SAVINGS;
    }

    const yearlyMiles = milesDriven * getPeriodMultiplier(milesPer);
    const yearlyTradeCost = calculateAnnualFuelCost(yearlyMiles, tradeInMpg, tradePrice);
    const yearlyNewVehicleCost = calculateAnnualFuelCost(yearlyMiles, newVehicleMpg, newVehiclePrice);

    const yearlySavings = yearlyTradeCost - yearlyNewVehicleCost;
    const monthlySavings = yearlySavings / 12;

    return {
        yearlySavings,
        monthlySavings,
        threeYearSavings: yearlySavings * 3
    };
};

/**
 * Dropdown options for miles per period selection
 */
export const MILES_PER_OPTIONS: IDropdownOption[] = [
    { label: 'Week', value: MILES_PER_WEEK },
    { label: 'Month', value: MILES_PER_MONTH },
    { label: 'Year', value: MILES_PER_YEAR }
];

/**
 * Format a number as USD currency.
 * @param amount - The amount to format
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

/**
 * Get semantic color for savings value.
 * @param value - Savings amount
 * @returns Green for positive, red for negative, undefined for neutral
 */
export const getSavingsColor = (value: number): string | undefined => {
    if (value > 0) return '#1a7f37';
    if (value < 0) return '#d32f2f';

    return undefined;
};

/** Regex pattern for valid numeric input: one or more digits with optional single decimal point */
const VALID_NUMERIC_REGEX = /^\d+\.?\d*$/;

/**
 * Checks if the input is a valid numeric string for user input.
 * Allows partial inputs during typing (e.g., "5.", "12.") and complete positive numbers.
 * Rejects zero, letters, special characters, and multiple decimals.
 * @param value - The input value to check
 * @returns true if valid numeric input, false otherwise
 */
export const isValidNumericInput = (value: string): boolean => {
    // Must match basic numeric pattern (digits with optional decimal)
    if (!VALID_NUMERIC_REGEX.test(value)) return false;

    // Allow partial decimal input during typing (e.g., "5.")
    if (/^\d+\.$/.test(value)) return true;

    // Parse and validate it's a positive number (not zero)
    const num = parseFloat(value);
    return !isNaN(num) && num > 0;
};

export const mergeTradeFormValues = (
    base: FuelSavingFormValues,
    persistedTrade: NonNullable<BaggageFuelEconomyPayload['trades']>[string] | undefined,
    persistedVehicle: NonNullable<BaggageFuelEconomyPayload['vehicles']>[string] | undefined,
    combinedFuelEconomy?: number
): FuelSavingFormValues => {
    // Resolve milesDriven/milesPer per-trade first, fall back to vehicle entry.
    // Using local variables avoids uncoverable branches from short-circuit ??.
    const resolvedMilesDriven = persistedTrade?.milesDriven ?? persistedVehicle?.milesDriven;
    const resolvedMilesPer = persistedTrade?.milesPer ?? persistedVehicle?.milesPer;

    return {
        ...base,
        ...(persistedTrade?.tradeInMpg != null && { tradeInMpg: String(persistedTrade.tradeInMpg) }),
        ...(persistedTrade?.fuelPricePerGallon != null && { tradePricePerGallon: String(persistedTrade.fuelPricePerGallon) }),
        ...(resolvedMilesDriven != null && { milesDriven: String(resolvedMilesDriven) }),
        ...(resolvedMilesPer && { milesPer: resolvedMilesPer.toUpperCase() as Period }),
        ...(persistedVehicle?.newVehicleMpg != null && { newVehicleMpg: String(persistedVehicle.newVehicleMpg) }),
        ...(persistedVehicle?.fuelPricePerGallon != null && {
            newVehiclePricePerGallon: String(persistedVehicle.fuelPricePerGallon)
        }),
        ...(!persistedVehicle?.newVehicleMpg && combinedFuelEconomy != null && { newVehicleMpg: String(combinedFuelEconomy) })
    };
};

export const buildTradeValuesByVehicle = (
    vehicleTabs: VehicleTab[],
    trades: TradeSectionData[],
    baggage?: BaggageFuelEconomyPayload
): Record<string, Record<string, FuelSavingFormValues>> =>
    // TODO: O(V*T) — pre-resolve trade baggage entries outside the vehicle loop to avoid repeated lookups per vehicle
    Object.fromEntries(
        vehicleTabs.map((vehicle) => {
            const persistedVehicle = baggage?.vehicles?.[vehicle.id];
            return [
                vehicle.id,
                Object.fromEntries(
                    trades.map((trade) => [
                        trade.id,
                        mergeTradeFormValues(
                            trade.values,
                            trade.vin ? baggage?.trades?.[trade.vin] : undefined,
                            persistedVehicle,
                            vehicle.combinedFuelEconomy
                        )
                    ])
                )
            ];
        })
    );

export const prefillTradeInMpgValues = (
    previousByVehicle: Record<string, Record<string, FuelSavingFormValues>>,
    trades: TradeSectionData[],
    tradeinVehiclesVin: VehicleFuelEconomy[]
): Record<string, Record<string, FuelSavingFormValues>> => {
    // TODO: too complex — revisit to simplify nested Object.entries iteration and trades.find per entry
    let hasChanges = false;

    const updated = Object.fromEntries(
        Object.entries(previousByVehicle).map(([vehicleId, vehicleTradeValues]) => {
            const updatedTradeValues = Object.fromEntries(
                Object.entries(vehicleTradeValues).map(([tradeId, values]) => {
                    const trade = trades.find((t) => t.id === tradeId);
                    if (!trade?.vin || values.tradeInMpg) {
                        return [tradeId, values];
                    }
                    const tradeInMpg = findMpgByVin(trade.vin, tradeinVehiclesVin);
                    if (!tradeInMpg) {
                        return [tradeId, values];
                    }
                    hasChanges = true;
                    return [tradeId, { ...values, tradeInMpg }];
                })
            );
            return [vehicleId, updatedTradeValues];
        })
    );

    return hasChanges ? updated : previousByVehicle;
};

/**
 * Prefill newVehicleMpg from the fuel economy API response (sellingVehiclesVin).
 * Only prefills if the field is currently empty — does not overwrite user input or baggage values.
 * Iterates vehicle tabs by vehicleId (which matches the VIN) and updates all trade rows for that tab.
 */
export const prefillNewVehicleMpgValues = (
    previousByVehicle: Record<string, Record<string, FuelSavingFormValues>>,
    vehicleTabs: VehicleTab[],
    sellingVehiclesVin: VehicleFuelEconomy[]
): Record<string, Record<string, FuelSavingFormValues>> => {
    let hasChanges = false;

    const updated = Object.fromEntries(
        Object.entries(previousByVehicle).map(([vehicleId, vehicleTradeValues]) => {
            // vehicleId is the VIN — look it up in the API response
            const newVehicleMpg = findMpgByVin(vehicleId, sellingVehiclesVin);
            if (!newVehicleMpg) {
                return [vehicleId, vehicleTradeValues];
            }

            const updatedTradeValues = Object.fromEntries(
                Object.entries(vehicleTradeValues).map(([tradeId, values]) => {
                    // Only prefill if not already set (baggage or user input takes priority)
                    if (values.newVehicleMpg) {
                        return [tradeId, values];
                    }
                    hasChanges = true;
                    return [tradeId, { ...values, newVehicleMpg }];
                })
            );
            return [vehicleId, updatedTradeValues];
        })
    );

    return hasChanges ? updated : previousByVehicle;
};

/**
 * Backfill newVehiclePricePerGallon from the persisted baggage.
 *
 * The form values are seeded from baggage only when a tab is first built (on slideout open
 * or when a new tab appears). If the baggage slice becomes available *after* a tab has already
 * been initialized (e.g. scenario tabs load asynchronously on reopen), the tab's price would
 * otherwise stay blank — and since newVehiclePricePerGallon has no other source (unlike
 * newVehicleMpg, which is also prefilled from the fuel-economy API), it would be lost and could
 * then be overwritten by a subsequent persist-on-change. This keeps the form in sync with
 * baggage as soon as it arrives.
 *
 * Only fills when the field is currently empty — never overwrites user input.
 * Iterates vehicle tabs by vehicleId (which matches the VIN) and updates all trade rows for that tab.
 */
export const backfillNewVehiclePricePerGallonValues = (
    previousByVehicle: Record<string, Record<string, FuelSavingFormValues>>,
    baggage: BaggageFuelEconomyPayload | undefined
): Record<string, Record<string, FuelSavingFormValues>> => {
    const baggageVehicles = baggage?.vehicles;
    if (!baggageVehicles) {
        return previousByVehicle;
    }

    let hasChanges = false;

    const updated = Object.fromEntries(
        Object.entries(previousByVehicle).map(([vehicleId, vehicleTradeValues]) => {
            // vehicleId is the VIN — look it up in the persisted baggage
            const persistedPrice = baggageVehicles[vehicleId]?.fuelPricePerGallon;
            if (persistedPrice == null) {
                return [vehicleId, vehicleTradeValues];
            }

            const updatedTradeValues = Object.fromEntries(
                Object.entries(vehicleTradeValues).map(([tradeId, values]) => {
                    // Only backfill if not already set (user input / earlier baggage read takes priority)
                    if (values.newVehiclePricePerGallon) {
                        return [tradeId, values];
                    }
                    hasChanges = true;
                    return [tradeId, { ...values, newVehiclePricePerGallon: String(persistedPrice) }];
                })
            );
            return [vehicleId, updatedTradeValues];
        })
    );

    return hasChanges ? updated : previousByVehicle;
};
